import React from 'react';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@components/Atoms/Skeleton';
import { useDistrictCurriculumExams, SelectedExam } from './hooks';

interface ExamSelectorProps {
  onExamSelect: (exam: SelectedExam | null) => void;
  selectedExam: SelectedExam | null;
  districtId: number | undefined;
  curriculumId: number | undefined;
}

const ExamSelector: React.FC<ExamSelectorProps> = ({
  onExamSelect,
  selectedExam,
  districtId,
  curriculumId,
}) => {
  const { data: examSessions, isLoading: isLoadingExams } = useDistrictCurriculumExams(
    districtId,
    curriculumId,
  );
  // Auto-select first exam when exams load
  React.useEffect(() => {
    if (examSessions && examSessions.length > 0 && !selectedExam) {
      const firstExam = examSessions[0];
      if (firstExam) {
        onExamSelect({
          id: firstExam.id,
          examName: firstExam.examName,
          examType: firstExam.examType,
          examDate: firstExam.examDate,
          examId: firstExam.exam?.id || firstExam.id,
        });
      }
    }
  }, [examSessions, selectedExam, onExamSelect]);

  const handleExamChange = (event: SelectChangeEvent<number>) => {
    const examSessionId = event.target.value as number;
    const examSession = examSessions?.find((e) => e.id === examSessionId);
    if (examSession) {
      onExamSelect({
        id: examSession.id,
        examName: examSession.examName,
        examType: examSession.examType,
        examDate: examSession.examDate,
        examId: examSession.exam?.id || examSession.id,
      });
    }
  };

  if (isLoadingExams) {
    return (
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="rectangular" width={300} height={56} />
      </Box>
    );
  }

  if (!districtId) {
    return null;
  }

  if (!examSessions || examSessions.length === 0) {
    return (
      <Box sx={{ p: 2, mb: 3, bgcolor: 'warning.light', borderRadius: 1 }}>
        <Typography variant="body2">
          No exam sessions available for this district and this curriculum.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <FormControl sx={{ minWidth: 300 }} size="small">
        <InputLabel id="exam-select-label">Exam</InputLabel>
        <Select
          labelId="exam-select-label"
          id="exam-select"
          value={selectedExam?.id || ''}
          label="Exam"
          onChange={handleExamChange}
        >
          {examSessions &&
            examSessions.map((examSession) => (
              <MenuItem key={examSession.id} value={examSession.id}>
                {examSession.examName}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ExamSelector;
